import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/Components/ui/dialog';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { ArrowLeft, RefreshCw, Key, AlertTriangle, CheckCircle, UserPlus, LogOut, Edit, Trash2 } from 'lucide-react';

interface User {
  id: string;
  email: string;
  userName: string;
  forename: string;
  surname: string;
  displayName: string;
  emailConfirmed: boolean;
  lockoutEnabled: boolean;
  lockoutEnd: string | null;
}

interface Tenant {
  tenantId: string;
  companyName: string;
}

export default function UserManager() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState<User[]>([]);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    forename: '',
    surname: '',
    displayName: ''
  });

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editSuccess, setEditSuccess] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editUserData, setEditUserData] = useState({
    email: '',
    forename: '',
    surname: '',
    displayName: ''
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);

  useEffect(() => {
    if (tenantId) {
      fetchTenantInfo();
      fetchUsers();
    }
  }, [tenantId]);

  const fetchTenantInfo = async () => {
    try {
      const response = await fetch(`/api/Admin/tenants/${tenantId}`);
      if (response.ok) {
        const data = await response.json();
        setTenant({
          tenantId: data.tenant.tenantId,
          companyName: data.tenant.name
        });
      }
    } catch (err) {
      console.error('Error fetching tenant info:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/Admin/tenants/${tenantId}/users`);
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        setError('Failed to load users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Network error while loading users');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordClick = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setResetSuccess(false);
    setResetDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !tenantId) return;

    // Validate passwords
    if (newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    try {
      setResetting(true);
      const response = await fetch(
        `/api/Admin/tenants/${tenantId}/users/${selectedUser.id}/reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ newPassword }),
        }
      );

      if (response.ok) {
        setResetSuccess(true);
        setTimeout(() => {
          setResetDialogOpen(false);
          setSelectedUser(null);
          setNewPassword('');
          setConfirmPassword('');
        }, 2000);
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Failed to reset password'}`);
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      alert('Network error while resetting password');
    } finally {
      setResetting(false);
    }
  };

  const handleCreateUserClick = () => {
    setNewUserData({
      email: '',
      password: '',
      confirmPassword: '',
      forename: '',
      surname: '',
      displayName: ''
    });
    setCreateSuccess(false);
    setCreateDialogOpen(true);
  };

  const handleCreateUser = async () => {
    if (!tenantId) return;

    // Validate passwords
    if (newUserData.password !== newUserData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (newUserData.password.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    if (!newUserData.email || !newUserData.forename || !newUserData.surname) {
      alert('Email, first name, and last name are required');
      return;
    }

    try {
      setCreating(true);
      const response = await fetch(
        `/api/Admin/tenants/${tenantId}/users`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: newUserData.email,
            password: newUserData.password,
            forename: newUserData.forename,
            surname: newUserData.surname,
            displayName: newUserData.displayName || `${newUserData.forename} ${newUserData.surname}`
          }),
        }
      );

      if (response.ok) {
        setCreateSuccess(true);
        setTimeout(() => {
          setCreateDialogOpen(false);
          fetchUsers(); // Refresh the list
        }, 2000);
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Failed to create user'}`);
      }
    } catch (err) {
      console.error('Error creating user:', err);
      alert('Network error while creating user');
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/Account/logout', {
        method: 'POST',
        credentials: 'include',
      });
      // Redirect to login page
      window.location.href = '/';
    } catch (err) {
      console.error('Error logging out:', err);
      alert('Failed to logout');
    }
  };

  const handleEditUserClick = (user: User) => {
    setEditUser(user);
    setEditUserData({
      email: user.email,
      forename: user.forename,
      surname: user.surname,
      displayName: user.displayName || ''
    });
    setEditSuccess(false);
    setEditDialogOpen(true);
  };

  const handleEditUser = async () => {
    if (!tenantId || !editUser) return;

    if (!editUserData.email || !editUserData.forename || !editUserData.surname) {
      alert('Email, first name, and last name are required');
      return;
    }

    try {
      setEditing(true);
      const response = await fetch(
        `/api/Admin/tenants/${tenantId}/users/${editUser.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: editUserData.email,
            forename: editUserData.forename,
            surname: editUserData.surname,
            displayName: editUserData.displayName || `${editUserData.forename} ${editUserData.surname}`
          }),
        }
      );

      if (response.ok) {
        setEditSuccess(true);
        setTimeout(() => {
          setEditDialogOpen(false);
          fetchUsers(); // Refresh the list
        }, 2000);
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Failed to update user'}`);
      }
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Network error while updating user');
    } finally {
      setEditing(false);
    }
  };

  const handleDeleteUserClick = (user: User) => {
    setDeleteUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!tenantId || !deleteUser) return;

    try {
      setDeleting(true);
      const response = await fetch(
        `/api/Admin/tenants/${tenantId}/users/${deleteUser.id}`,
        {
          method: 'DELETE',
        }
      );

      if (response.ok) {
        alert('User deleted successfully');
        setDeleteDialogOpen(false);
        fetchUsers(); // Refresh the list
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Failed to delete user'}`);
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Network error while deleting user');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4 mb-4">
            <Button variant="outline" onClick={() => navigate('/admin/tenants')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Tenants
            </Button>
          </div>
          {tenant && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Managing Tenant:</span>
                <span className="text-lg font-bold text-blue-900 dark:text-blue-100">{tenant.companyName}</span>
                <span className="text-xs text-blue-600 dark:text-blue-400">({tenant.tenantId})</span>
              </div>
            </div>
          )}
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-3xl">User Management</CardTitle>
              <CardDescription>
                {tenant ? `Manage users and reset passwords for ${tenant.companyName}` : 'Manage users and reset passwords'}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreateUserClick} variant="default">
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
              <Button onClick={fetchUsers} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={handleLogout} variant="destructive">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {users.map((user) => (
              <Card key={user.id} className="border-l-4 border-l-blue-500">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">
                          {user.displayName || `${user.forename} ${user.surname}`.trim() || user.userName}
                        </h3>
                        {!user.emailConfirmed && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                            Unverified Email
                          </span>
                        )}
                        {user.lockoutEnd && new Date(user.lockoutEnd) > new Date() && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                            Locked Out
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                        <div>
                          <span className="font-medium">Email:</span> {user.email}
                        </div>
                        <div>
                          <span className="font-medium">Username:</span> {user.userName}
                        </div>
                        <div>
                          <span className="font-medium">User ID:</span> {user.id}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditUserClick(user)}
                        title="Edit user details"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResetPasswordClick(user)}
                        title="Reset user password"
                      >
                        <Key className="h-4 w-4 mr-2" />
                        Reset Password
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteUserClick(user)}
                        title="Delete user"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {users.length === 0 && !loading && (
              <div className="text-center py-12 text-muted-foreground">
                No users found for this tenant
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {resetSuccess ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Password Reset Successfully
                </>
              ) : (
                <>
                  <Key className="h-5 w-5 text-blue-600" />
                  Reset User Password
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {resetSuccess ? (
            <div className="py-4 text-center">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <p className="text-lg font-medium text-green-700">
                Password has been reset successfully!
              </p>
            </div>
          ) : (
            <>
              <div className="py-4">
                <p className="mb-4">
                  Reset password for: <strong>{selectedUser?.email}</strong>
                </p>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <div className="font-medium">Important:</div>
                    <div>You are manually setting a new password for this user. Make sure to communicate the new password securely.</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                      disabled={resetting}
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      disabled={resetting}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setResetDialogOpen(false)}
                  disabled={resetting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleResetPassword}
                  disabled={resetting || !newPassword || !confirmPassword}
                >
                  {resetting ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <Key className="h-4 w-4 mr-2" />
                      Reset Password
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {createSuccess ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  User Created Successfully
                </>
              ) : (
                <>
                  <UserPlus className="h-5 w-5 text-blue-600" />
                  Create New User
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {createSuccess ? (
            <div className="py-6 text-center">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <p className="text-lg font-semibold text-green-700">
                User created successfully!
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                The new user can now log in to the system.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@example.com"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="forename">First Name *</Label>
                  <Input
                    id="forename"
                    type="text"
                    placeholder="John"
                    value={newUserData.forename}
                    onChange={(e) => setNewUserData({ ...newUserData, forename: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="surname">Last Name *</Label>
                  <Input
                    id="surname"
                    type="text"
                    placeholder="Doe"
                    value={newUserData.surname}
                    onChange={(e) => setNewUserData({ ...newUserData, surname: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="displayName">Display Name (Optional)</Label>
                  <Input
                    id="displayName"
                    type="text"
                    placeholder="Leave empty to use First + Last"
                    value={newUserData.displayName}
                    onChange={(e) => setNewUserData({ ...newUserData, displayName: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="At least 6 characters"
                    value={newUserData.password}
                    onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Re-enter password"
                    value={newUserData.confirmPassword}
                    onChange={(e) => setNewUserData({ ...newUserData, confirmPassword: e.target.value })}
                  />
                </div>

                {newUserData.password && newUserData.confirmPassword && 
                 newUserData.password !== newUserData.confirmPassword && (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <AlertTriangle className="h-4 w-4" />
                    <span>Passwords do not match</span>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setCreateDialogOpen(false)}
                  disabled={creating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateUser}
                  disabled={creating || !newUserData.email || !newUserData.forename || 
                           !newUserData.surname || !newUserData.password || 
                           !newUserData.confirmPassword || 
                           newUserData.password !== newUserData.confirmPassword}
                >
                  {creating ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Create User
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editSuccess ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  User Updated Successfully
                </>
              ) : (
                <>
                  <Edit className="h-5 w-5 text-blue-600" />
                  Edit User
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {editSuccess ? (
            <div className="py-6 text-center">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <p className="text-lg font-semibold text-green-700">
                User updated successfully!
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email Address *</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    placeholder="user@example.com"
                    value={editUserData.email}
                    onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-forename">First Name *</Label>
                  <Input
                    id="edit-forename"
                    type="text"
                    placeholder="John"
                    value={editUserData.forename}
                    onChange={(e) => setEditUserData({ ...editUserData, forename: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-surname">Last Name *</Label>
                  <Input
                    id="edit-surname"
                    type="text"
                    placeholder="Doe"
                    value={editUserData.surname}
                    onChange={(e) => setEditUserData({ ...editUserData, surname: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-displayName">Display Name (Optional)</Label>
                  <Input
                    id="edit-displayName"
                    type="text"
                    placeholder="Leave empty to use First + Last"
                    value={editUserData.displayName}
                    onChange={(e) => setEditUserData({ ...editUserData, displayName: e.target.value })}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setEditDialogOpen(false)}
                  disabled={editing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleEditUser}
                  disabled={editing || !editUserData.email || !editUserData.forename || !editUserData.surname}
                >
                  {editing ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Edit className="h-4 w-4 mr-2" />
                      Update User
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Delete User
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Are you sure you want to delete this user? This action cannot be undone.
            </p>
            {deleteUser && (
              <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="font-semibold">{deleteUser.displayName || `${deleteUser.forename} ${deleteUser.surname}`}</p>
                <p className="text-sm text-muted-foreground">{deleteUser.email}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
